import PrimaryInnerGlowButton, { PrimaryDefaultButton } from '@/components/buttons/primary';
import FrontPinkButton from '@/components/buttons/primary/FrontPinkButton';
import FilterBlock from '@/components/FilterBlock';
import { VisuallyHidden } from '@/components/Helpers';
import Image from '@/components/Image';
import { Container } from '@/components/layout/Container';
import Link from '@/components/Link';
import { PRIMARY_NAV_HEIGHT } from '@/components/navigation/PrimaryNavigation';
import { HELP_CENTER } from '@/constants/routes';
import { GlobalContext } from '@/context';
import { ResourcesHomepageQuery } from '@/graphql';
import ResourcesHomepageQueryDocument from '@/graphql/queries/ResourcesHomepage.gql';
import gtm from '@/lib/gtm';
import { getStaticPageProps } from '@/lib/next';
import { getSingle, maybeNotSet, notEmpty } from '@/lib/typescript-helpers';
import { COLORS } from '@/styles/color';
import { marginX, mediaBreakpointUp, paddingX, paddingY } from '@/styles/layout/utils';
import { FONT_WEIGHTS, TEXT_SIZES } from '@/styles/typography';
import { customTextSize, remCalc } from '@/styles/typography/utils';
import MasterTemplate from '@/templates/MasterTemplate';
import { camelCase, startCase } from 'lodash';
import { Fragment, useContext, useMemo, useState } from 'react';
import styled from 'styled-components';
import { ArrayParam, useQueryParam } from 'use-query-params';

// #region - Components

const PageWrapper = styled.div`
  padding-top: ${remCalc(PRIMARY_NAV_HEIGHT)};
`;

const Hero = styled.header`
  background-color: ${COLORS.BLUE.TINT};
  padding: ${remCalc(16)};
  border-radius: ${remCalc(10)};

  margin-left: calc(var(--breakpoint-padding-x) * -0.5);
  margin-right: calc(var(--breakpoint-padding-x) * -0.5);
  margin-bottom: ${remCalc(24)};

  display: grid;
  grid-template-columns: 100%;
  grid-template-rows: auto auto;

  row-gap: ${remCalc(24)};

  ${mediaBreakpointUp('tablet')} {
    grid-template-columns: 1fr minmax(0, ${remCalc(300)});
    grid-template-rows: auto;
    align-items: center;

    column-gap: ${remCalc(16)};

    ${marginX(0)}
  }

  ${mediaBreakpointUp('desktop')} {
    margin-bottom: ${remCalc(60)};
    grid-template-columns: 3fr 2fr;
  }

  ${mediaBreakpointUp('desktopWide')} {
    grid-template-columns: 1fr 1fr;
  }
`;

const HeroLeft = styled.div`
  ${paddingX(16)}
  ${paddingY(24)}

  ${mediaBreakpointUp('desktop')} {
    ${paddingX(84)}
    ${paddingY(48)}
  }

  ${PrimaryInnerGlowButton} {
    margin-top: ${remCalc(16)};
  }
`;

const HeroTitle = styled.p`
  ${customTextSize(40, 42)}
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  margin-bottom: ${remCalc(16)};

  small {
    display: block;
    ${customTextSize(16, 26)}
    font-weight: ${FONT_WEIGHTS.NORMAL};
    margin-bottom: ${remCalc(8)};
  }
`;

const HeroRight = styled.div`
  background-color: ${COLORS.WHITE};
  border-radius: ${remCalc(10)};
  padding: ${remCalc(16)};
`;

const HeroImage = styled(Image)`
  border-radius: ${remCalc(10)};
  overflow: hidden;
  width: 100%;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 100%;
  grid-template-rows: auto auto;
  grid-gap: ${remCalc(32)};

  ${mediaBreakpointUp('desktop')} {
    grid-template-columns: ${remCalc(288)} auto;
    grid-template-rows: auto;
  }
`;

const StyledFilterBlock = styled(FilterBlock)`
  position: sticky;
  top: ${remCalc(PRIMARY_NAV_HEIGHT + 20)};
`;

const ResourceCardGrid = styled.div`
  display: grid;
  grid-template-columns: 100%;
  grid-auto-rows: auto;
  column-gap: ${remCalc(16)};
  row-gap: ${remCalc(32)};

  ${mediaBreakpointUp('tablet')} {
    grid-template-columns: repeat(2, 1fr);
  }

  ${mediaBreakpointUp('desktopWide')} {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const ResourceCardImage = styled.div`
  aspect-ratio: 283/200;
  width: 100%;
  position: relative;
  border-radius: ${remCalc(10)};
  overflow: hidden;
  background-color: ${COLORS.GRAY.HIGHLIGHT};
  transition: border-radius 0.15s ease-in-out;

  img {
    object-fit: cover;
    object-position: center;
  }
`;

const ResourceCardContent = styled.div`
  padding: ${remCalc(16, 8)};

  h3 {
    ${customTextSize(24, 30)}
    font-weight: ${FONT_WEIGHTS.MEDIUM};
    margin-bottom: ${remCalc(8)};

    small {
      display: block;
      ${customTextSize(14, 24)}
      font-weight: ${FONT_WEIGHTS.NORMAL};
      color: ${COLORS.GRAY.BASE};
    }
  }
`;

const ResourceCardCustomerLogo = styled.div`
  position: relative;
  margin: ${remCalc(16)} 0 0 0;

  img {
    width: auto;
    max-height: 24px;
  }
`;

const ResourceCard = styled.div`
  background-color: transparent;
  border-radius: ${remCalc(10)};
  max-width: ${remCalc(288)};
  margin: 0 auto;
  transition: background-color 0.15s ease-in-out;

  ${mediaBreakpointUp('tablet')} {
    max-width: none;
  }

  &:hover {
    background-color: ${COLORS.GRAY.HIGHLIGHT};

    ${ResourceCardImage} {
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    }
  }

  & > a {
    display: block;
  }
`;

const FeaturedResourceCard = styled.div`
  grid-column: 1 / -1;
  background-color: ${COLORS.BLUE.TINT};
  padding: ${remCalc(16)};
  border-radius: ${remCalc(10)};

  display: grid;
  grid-template-columns: 100%;
  grid-template-rows: auto auto;

  row-gap: ${remCalc(24)};

  ${mediaBreakpointUp('tablet')} {
    grid-template-columns: 1fr minmax(0, ${remCalc(300)});
    grid-template-rows: auto;
    align-items: center;
    column-gap: ${remCalc(16)};
    ${marginX(0)}
  }

  ${mediaBreakpointUp('desktopWide')} {
    grid-template-columns: 1fr 1fr;
  }
`;

const FeaturedResourceCardLeft = styled.div`
  ${paddingX(16)}
  ${paddingY(24)}

  ${mediaBreakpointUp('desktopWide')} {
    ${paddingX(32)}
    ${paddingY(32)}
  }

  ${PrimaryInnerGlowButton} {
    margin-top: ${remCalc(16)};
  }
`;

const FeaturedResourceCardTitle = styled.p`
  ${customTextSize(40, 42)}
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  margin-bottom: ${remCalc(16)};

  small {
    display: block;
    ${customTextSize(16, 26)}
    font-weight: ${FONT_WEIGHTS.NORMAL};
    margin-bottom: ${remCalc(8)};
  }
`;

const FeaturedResourceCardRight = styled.div`
  background-color: ${COLORS.WHITE};
  border-radius: ${remCalc(10)};
  padding: ${remCalc(16)};
`;

const FeaturedResourceCardImage = styled(Image)`
  border-radius: ${remCalc(10)};
  overflow: hidden;
  width: 100%;
`;

const CalloutBlock = styled.div`
  ${paddingX(32)};
  ${paddingY(32)};
  margin-bottom: ${remCalc(40)};
  margin-top: ${remCalc(40)};

  background: linear-gradient(209deg, #001b38 23.25%, #006eb2 161.08%, #001b38);
  border-radius: ${remCalc(10)};

  display: flex;
  flex-direction: column;
  align-items: center;
  row-gap: 2rem;

  ${mediaBreakpointUp('desktop')} {
    margin-top: ${remCalc(100)};
  }

  ${mediaBreakpointUp('desktopWide')} {
    flex-direction: row;
    row-gap: 0;
    column-gap: ${remCalc(104)};
    ${paddingX(104)};
    ${paddingY(42)};
  }
`;

const CalloutBlockLeft = styled.div`
  flex-grow: 1;
`;

const CalloutBlockRight = styled.div`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  gap: ${remCalc(16)};

  ${mediaBreakpointUp('tablet')} {
    flex-direction: row;
  }
`;

const CalloutBlockText = styled.p`
  ${TEXT_SIZES[38]};

  font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  color: ${COLORS.WHITE};
  text-align: center;

  ${mediaBreakpointUp('desktopWide')} {
    text-align: left;
  }
`;

const StyledPrimaryDefaultButton = styled(PrimaryDefaultButton)`
  --button-color: ${COLORS.WHITE};
  --button-text-color: ${COLORS.BLACK};
  --button-color-hover: ${COLORS.BLUE.LOWLIGHT};
  --button-text-color-hover: ${COLORS.WHITE};

  width: 100%;

  ${mediaBreakpointUp('tablet')} {
    width: auto;
  }
`;

const LargeCalloutBlock = styled.div`
  padding: ${remCalc(32)};
  margin-bottom: ${remCalc(40)};

  border-radius: ${remCalc(10)};

  display: flex;
  flex-direction: column;
  align-items: center;
  row-gap: ${remCalc(60)};

  ${mediaBreakpointUp('tablet')} {
    padding: ${remCalc(50)};
  }

  ${mediaBreakpointUp('desktopWide')} {
    ${paddingX(40)};
    ${paddingY(70)};
  }

  p {
    ${customTextSize(40, 42)};
    font-weight: ${FONT_WEIGHTS.SEMIBOLD};
    color: ${COLORS.BLACK};
    text-align: center;
    max-width: ${remCalc(1100)};
    margin: 0 auto;

    ${mediaBreakpointUp('desktopWide')} {
      ${customTextSize(44, 42)};
    }
  }

  ${StyledPrimaryDefaultButton} {
    --button-color: ${COLORS.WHITE};
    --button-text-color: ${COLORS.BLACK};
    --button-color-hover: ${COLORS.BLACK};
    --button-text-color-hover: ${COLORS.WHITE};
  }
`;

const ShowAllWrapper = styled.div`
  grid-column: 1 / -1;
  text-align: center;
  padding: ${remCalc(24, 0)};

  button {
    color: ${COLORS.BLUE.LOWLIGHT};
    font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  }
`;

const CTASection = styled.div`
  background: transparent;
  ${paddingY(40)};
`;

// #endregion - Components

interface ResourcesHomepageProps extends ResourcesHomepageQuery { }

const ResourcesHomepage: PageTemplate<ResourcesHomepageProps> = ({ entry, entries }) => {
  const { toggleGetStartedModal } = useContext(GlobalContext);
  const [selectedTypes, setSelectedTypes] = useQueryParam('type', ArrayParam);
  const [selectedTeams, setSelectedTeams] = useQueryParam('team', ArrayParam);
  const [selectedTopics, setSelectedTopics] = useQueryParam('topic', ArrayParam);
  const [selectedIndustries, setSelectedIndustries] = useQueryParam('industry', ArrayParam);
  const [showAll, setShowAll] = useState(false);

  const hasFiltersApplied = Boolean(
    selectedTypes?.length || selectedTeams?.length || selectedTopics?.length || selectedIndustries?.length,
  );

  const types = useMemo(() => {
    const types =
      entries
        ?.flatMap((entry) => {
          if (!entry?.type) {
            return null;
          }
          return camelCase(entry?.type) === entry?.type ? entry?.type : entry?.type?.toLowerCase();
        })
        .filter(notEmpty) ?? [];

    if (!types.length) return [];

    return [...new Set(types)].sort((a, b) => a.localeCompare(b));
  }, [entries]);

  const teams = useMemo(() => {
    const teams =
      entries
        ?.flatMap((entry) => {
          return entry?.teams;
        })
        .filter(notEmpty) ?? [];

    if (!teams.length) return [];

    return teams
      .reduce((all, team) => {
        if (!all.some(({ id }) => id === team.id)) {
          return [...all, team];
        }

        return all;
      }, [])
      .sort((a, b) => (a.title ?? '').localeCompare(b.title ?? ''));
  }, [entries]);

  const topics = useMemo(() => {
    const topics =
      entries
        ?.flatMap((entry) => {
          return entry?.topics;
        })
        .filter(notEmpty) ?? [];

    if (!topics.length) return [];

    return topics
      .reduce((all, topic) => {
        if (!all.some(({ id }) => id === topic.id)) {
          return [...all, topic];
        }

        return all;
      }, [])
      .sort((a, b) => (a.title ?? '').localeCompare(b.title ?? ''));
  }, [entries]);

  const industries = useMemo(() => {
    const industries =
      entries
        ?.flatMap((entry) => {
          return entry?.industries;
        })
        .filter(notEmpty) ?? [];

    if (!industries.length) return [];

    return industries
      .reduce((all, industry) => {
        if (!all.some(({ id }) => id === industry.id)) {
          return [...all, industry];
        }

        return all;
      }, [])
      .sort((a, b) => (a.title ?? '').localeCompare(b.title ?? ''));
  }, [entries]);

  const filterSelections = useMemo(() => {
    let filterIds: string[] = [];

    if (selectedTypes?.length) {
      selectedTypes.filter(notEmpty).forEach((slug) => {
        if (types.includes(slug)) {
          filterIds.push(slug);
        }
      });
    }

    if (selectedTeams?.length) {
      selectedTeams.filter(notEmpty).forEach((slug) => {
        const match = teams.find((team) => team.slug === slug);

        if (match) {
          filterIds.push(match.id!);
        }
      });
    }

    if (selectedTopics?.length) {
      selectedTopics.filter(notEmpty).forEach((slug) => {
        const match = topics.find((topic) => topic.slug === slug);

        if (match) {
          filterIds.push(match.id!);
        }
      });
    }

    if (selectedIndustries?.length) {
      selectedIndustries.filter(notEmpty).forEach((slug) => {
        const match = industries.find((industry) => industry.slug === slug);

        if (match) {
          filterIds.push(match.id!);
        }
      });
    }

    return filterIds;
  }, [selectedTypes, types, selectedTeams, teams, selectedTopics, topics, selectedIndustries, industries]);

  const filteredResources = useMemo(() => {
    if (!entries) return null;

    const resources = [...entries.filter(notEmpty)];

    if (!hasFiltersApplied) return resources;

    return resources
      .filter((resource) =>
        selectedTypes?.length && resource.type
          ? selectedTypes
            .filter(notEmpty)
            .map((t) => t.toLowerCase())
            .includes(resource.type.toLowerCase())
          : true,
      )
      .filter((resource) =>
        selectedTeams?.length
          ? selectedTeams.some((slug) => resource.teams?.some((team) => team?.slug === slug))
          : true,
      )
      .filter((resource) =>
        selectedTopics?.length
          ? selectedTopics.some((slug) => resource.topics?.some((topic) => topic?.slug === slug))
          : true,
      )
      .filter((resource) =>
        selectedIndustries?.length
          ? selectedIndustries.some((slug) => resource.industries?.some((industry) => industry?.slug === slug))
          : true,
      );
  }, [entries, selectedTypes, selectedTeams, selectedTopics, selectedIndustries]);

  const featuredHeroResource = useMemo(() => {
    if (!entries) return null;

    return entries.filter(notEmpty).find(({ id }) => id === entry?.featuredHeroResource?.[0]?.id);
  }, [entry?.featuredHeroResource, entries]);

  const featuredResource = useMemo(() => {
    if (!entries) return null;

    return entries.filter(notEmpty).find(({ id }) => id === entry?.featuredResource?.[0]?.id);
  }, [entry?.featuredResource, entries]);

  return (
    <PageWrapper>
      <Container>
        {featuredHeroResource && (
          <Hero>
            <HeroLeft>
              <HeroTitle>
                <small>{startCase(featuredHeroResource.type ?? 'Resource')}</small>
                <VisuallyHidden>:&nbsp;</VisuallyHidden>
                <span>{featuredHeroResource.title ?? ''}</span>
              </HeroTitle>

              {featuredHeroResource.summary && <p>{featuredHeroResource.summary}</p>}

              <Link href={featuredHeroResource.url ?? '#'}>
                <PrimaryInnerGlowButton
                  as="a"
                  onClick={() => {
                    gtm.track('click', {
                      element_type: 'link',
                      location: 'Resources Homepage Hero',
                      element_label: maybeNotSet(entry?.heroCtaText),
                      resource_title: maybeNotSet(featuredHeroResource.title),
                    });
                  }}
                >
                  {entry?.heroCtaText ?? 'Check it out'}
                </PrimaryInnerGlowButton>
              </Link>
            </HeroLeft>

            <HeroRight>
              {featuredHeroResource.coverImage?.length && (
                <HeroImage
                  src={featuredHeroResource.coverImage?.[0]?.url!}
                  width={featuredHeroResource.coverImage?.[0]?.width!}
                  height={featuredHeroResource.coverImage?.[0]?.height!}
                  alt=""
                />
              )}
            </HeroRight>
          </Hero>
        )}

        <Grid>
          <div>
            <StyledFilterBlock
              categories={[
                {
                  title: 'Types',
                  slug: 'types',
                  filters: types.map((type) => ({
                    id: type,
                    title: type.toLowerCase() === 'research' ? startCase(type) : `${startCase(type)}s`,
                    slug: type,
                  })),
                },
                {
                  title: 'Teams',
                  slug: 'teams',
                  filters: teams.map((team) => ({
                    id: team.id!,
                    title: team.title!,
                    slug: team.slug!,
                  })),
                },
                {
                  title: 'Topics',
                  slug: 'topics',
                  filters: topics.map((topic) => ({
                    id: topic.id!,
                    title: topic.title!,
                    slug: topic.slug!,
                  })),
                },
                {
                  title: 'Industries',
                  slug: 'industries',
                  filters: industries.map((industry) => ({
                    id: industry.id!,
                    title: industry.title!,
                    slug: industry.slug!,
                  })),
                },
              ]}
              collapsible={false}
              selections={filterSelections}
              onFilterToggle={(category, filter, selected) => {
                switch (category.slug) {
                  case 'types':
                    if (selected) {
                      setSelectedTypes([...(selectedTypes ?? []), filter.slug]);
                    } else {
                      setSelectedTypes(selectedTypes?.filter((type) => type !== filter.slug));
                    }

                    break;

                  case 'teams':
                    if (selected) {
                      setSelectedTeams([...(selectedTeams ?? []), filter.slug]);
                    } else {
                      setSelectedTeams(selectedTeams?.filter((team) => team !== filter.slug));
                    }

                    break;

                  case 'topics':
                    if (selected) {
                      setSelectedTopics([...(selectedTopics ?? []), filter.slug]);
                    } else {
                      setSelectedTopics(selectedTopics?.filter((topic) => topic !== filter.slug));
                    }

                    break;

                  case 'industries':
                    if (selected) {
                      setSelectedIndustries([...(selectedIndustries ?? []), filter.slug]);
                    } else {
                      setSelectedIndustries(selectedIndustries?.filter((industry) => industry !== filter.slug));
                    }

                    break;
                }

                gtm.track(selected ? 'checked_filter' : 'unchecked_filter', {
                  location: 'Resources Homepage',
                  filter_title: maybeNotSet(filter.title),
                  category_title: maybeNotSet(category.title),
                });
              }}
            />
          </div>

          <ResourceCardGrid>
            {filteredResources?.slice(0, showAll ? undefined : 12).map((resource, index) => {
              const image = getSingle(resource.coverImage);
              const customer = 'customer' in resource ? resource.customer[0] : null;
              const customerLogo = customer ? customer?.customerLogo?.[0] : null;

              return (
                <Fragment key={`resource-card-${resource.id}-${index}`}>
                  <ResourceCard>
                    <Link href={resource.url ?? '#'}>
                      <a
                        onClick={() => {
                          gtm.track('click', {
                            location: 'Resources Homepage',
                            element_type: 'link',
                            element_label: maybeNotSet(resource.title),
                          });
                        }}
                      >
                        {image && (
                          <ResourceCardImage>
                            <Image src={image.url!} alt="" fill />
                          </ResourceCardImage>
                        )}

                        <ResourceCardContent>
                          <h3>
                            {resource.type && <small>{startCase(resource.type)}</small>}
                            <VisuallyHidden>:&nbsp;</VisuallyHidden>
                            {resource.title}
                          </h3>

                          {resource.summary && <p>{resource.summary ?? ''}</p>}

                          {customer && customerLogo && (
                            <ResourceCardCustomerLogo>
                              <Image
                                src={customerLogo.url!}
                                width={customerLogo.width!}
                                height={customerLogo.height!}
                                alt={`${customer.title} logo'`}
                              />
                            </ResourceCardCustomerLogo>
                          )}
                        </ResourceCardContent>
                      </a>
                    </Link>
                  </ResourceCard>

                  {featuredResource && !hasFiltersApplied && index + 1 === 6 && (
                    <FeaturedResourceCard>
                      <FeaturedResourceCardLeft>
                        <FeaturedResourceCardTitle>
                          <small>{startCase(featuredResource.type ?? 'Resource')}</small>
                          <VisuallyHidden>:&nbsp;</VisuallyHidden>
                          <span>{featuredResource.title}</span>
                        </FeaturedResourceCardTitle>

                        {featuredResource.summary && <p>{featuredResource.summary}</p>}

                        <Link href={featuredResource.url ?? '#'}>
                          <PrimaryInnerGlowButton
                            as="a"
                            onClick={() => {
                              gtm.track('click', {
                                location: 'Resources Homepage Featured Resource',
                                element_type: 'link',
                                element_label: maybeNotSet(entry?.ctaText),
                                resource_title: maybeNotSet(featuredResource.title),
                              });
                            }}
                          >
                            {entry?.ctaText ?? 'Check it out'}
                          </PrimaryInnerGlowButton>
                        </Link>
                      </FeaturedResourceCardLeft>

                      <FeaturedResourceCardRight>
                        <FeaturedResourceCardImage
                          src={featuredResource.coverImage?.[0]?.url!}
                          width={featuredResource.coverImage?.[0]?.width!}
                          height={featuredResource.coverImage?.[0]?.height!}
                          alt=""
                        />
                      </FeaturedResourceCardRight>
                    </FeaturedResourceCard>
                  )}
                </Fragment>
              );
            })}

            {!showAll && !hasFiltersApplied ? (
              <ShowAllWrapper>
                <button
                  type="button"
                  onClick={() => {
                    setShowAll(true);

                    gtm.track('click', {
                      element_type: 'button',
                      element_label: 'Show more/all',
                      location: 'Resources Homepage',
                    });
                  }}
                >
                  Show more ({(entries ?? []).length - 12})
                </button>
              </ShowAllWrapper>
            ) : null}
          </ResourceCardGrid>
        </Grid>

        <CalloutBlock>
          <CalloutBlockLeft>
            <CalloutBlockText>Are you already using Front? Learn more on our Help Center</CalloutBlockText>
          </CalloutBlockLeft>

          <CalloutBlockRight>
            <StyledPrimaryDefaultButton
              as="a"
              href={HELP_CENTER}
              target="_blank"
              onClick={() => {
                gtm.track('click', {
                  element_type: 'link',
                  element_label: 'Go to Help Center',
                  location: 'Resources Homepage Footer',
                });
              }}
            >
              Go to Help Center
            </StyledPrimaryDefaultButton>
          </CalloutBlockRight>
        </CalloutBlock>
      </Container>
      <CTASection>
        <Container>
          <LargeCalloutBlock>
            <p>Ready to build long-lasting customer relationships?</p>

            <FrontPinkButton
              onClick={() => {
                toggleGetStartedModal(true);

                gtm.track('click', {
                  element_type: 'button',
                  element_label: 'Get Started',
                  location: 'Resources Homepage Footer',
                });
              }}
            >
              Get started
            </FrontPinkButton>
          </LargeCalloutBlock>
        </Container>
      </CTASection>
    </PageWrapper>
  );
};

ResourcesHomepage.template = MasterTemplate;

export default ResourcesHomepage;

export const getStaticProps = getStaticPageProps('resources', async ({ client }) => {
  const props = await client.request<ResourcesHomepageQuery>(ResourcesHomepageQueryDocument);

  return { props };
});
